import React, { FC, useEffect, useState } from 'react';
import Slider from 'react-slick';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useInternshipSettings } from 'contexts';
import { INTRO_CAROUSEL_DATA } from 'consts';
import { BaseWrapper, PrimaryButton } from 'styles/common';

import {
    Container,
    ActionContainer,
    DateContainer,
    CloseBlock,
    DateContainerClosed,
    Bubble1,
    Bubble2,
    Bubble3,
    Bubble4,
    OpenedTitle,
    Closed,
    CarouselContainer,
    CarouselItem,
    ItemImage,
    ItemText,
} from './styled';

interface IntroProps {
    config: {
        until: Date | null;
        isOpened: boolean;
        introTitle: string;
        closeTitle?: string;
    };
}

const getCarouselItemWidth = (viewportWidth: number): number => {
    if (viewportWidth < 1024) {
        return 190;
    }

    if (viewportWidth < 1280) {
        return 280;
    }

    return 320;
};

const Intro: FC<IntroProps> = ({ config: { until, isOpened, introTitle, closeTitle } }) => {
    const { t } = useTranslation('pages/home');

    const { showRegistrationModal, showSubscriptionModal } = useInternshipSettings();

    const [carouselItemWidth, setCarouselItemWidth] = useState(320);

    useEffect(() => {
        const updateCarouselItemWidth = () => {
            const nextWidth = getCarouselItemWidth(window.innerWidth);

            setCarouselItemWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
        };

        updateCarouselItemWidth();

        window.addEventListener('resize', updateCarouselItemWidth);

        return () => {
            window.removeEventListener('resize', updateCarouselItemWidth);
        };
    }, []);

    const sliderSettings = {
        dots: false,
        arrows: false,
        infinite: true,

        speed: 1250,
        cssEase: 'ease',

        variableWidth: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0px',

        autoplay: true,
        autoplaySpeed: 3000,

        swipe: false,
        draggable: false,
        touchMove: false,
        focusOnSelect: false,
        accessibility: false,

        pauseOnHover: false,
        pauseOnFocus: false,
        pauseOnDotsHover: false,
    };

    return (
        <Container>
            <Bubble2 src={`${process.env.PUBLIC_URL}/assets/images/bubble2.png`} alt="Safeboard bubble image" />

            <Bubble4 src={`${process.env.PUBLIC_URL}/assets/images/bubble4.png`} alt="Safeboard bubble image" />

            <BaseWrapper styles={{ position: 'relative' }}>
                <Bubble1 src={`${process.env.PUBLIC_URL}/assets/images/bubble1.png`} alt="Safeboard bubble image" />

                <Bubble3
                    src={`${process.env.PUBLIC_URL}/assets/images/bubble3.png`}
                    alt="Safeboard bubble image"
                    isClosed={!isOpened}
                />
            </BaseWrapper>

            <BaseWrapper>
                <ActionContainer>
                    {isOpened && (
                        <CloseBlock>
                            <OpenedTitle>{introTitle}</OpenedTitle>

                            <ActionContainer>
                                <PrimaryButton
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={showRegistrationModal}
                                >
                                    {t('register')}
                                </PrimaryButton>

                                {until && (
                                    <DateContainer>
                                        {t('until', {
                                            date: new Date(until).toLocaleString('ru-RU', {
                                                day: 'numeric',
                                                month: 'long',
                                            }),
                                        })}
                                    </DateContainer>
                                )}
                            </ActionContainer>
                        </CloseBlock>
                    )}

                    {!isOpened && (
                        <CloseBlock>
                            <Closed>{t('closed')}</Closed>

                            <DateContainerClosed>{closeTitle}</DateContainerClosed>

                            <PrimaryButton
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={showSubscriptionModal}
                            >
                                {t('leave-contact')}
                            </PrimaryButton>
                        </CloseBlock>
                    )}
                </ActionContainer>
            </BaseWrapper>

            <CarouselContainer>
                <Slider key={carouselItemWidth} {...sliderSettings}>
                    {INTRO_CAROUSEL_DATA.map((item) => (
                        <CarouselItem key={item.id} style={{ width: carouselItemWidth }}>
                            <ItemImage src={item.image} alt={`Slide ${item.id}`} $isCompass={item.id === 4} />

                            <ItemText
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(item.text),
                                }}
                            />
                        </CarouselItem>
                    ))}
                </Slider>
            </CarouselContainer>
        </Container>
    );
};

export default Intro;
